import { useState, useEffect, useCallback } from 'react';
import scraperService, { TarifsLegalinData, ScraperMetadata } from '../services/scraper_service';
import TarifsMapperService, { TarifsMapping } from '../services/TarifsMapperService';

interface UseScrapedTarifsReturn {
  scrapedData: TarifsLegalinData | null;
  mappedTarifs: TarifsMapping | null;
  basePrices: { [key: string]: number };
  metadata: ScraperMetadata | null;
  loading: boolean;
  error: string | null;
  refreshTarifs: () => Promise<void>;
}

export const useScrapedTarifs = (): UseScrapedTarifsReturn => {
  const [scrapedData, setScrapedData] = useState<TarifsLegalinData | null>(null);
  const [mappedTarifs, setMappedTarifs] = useState<TarifsMapping | null>(null);
  const [basePrices, setBasePrices] = useState<{ [key: string]: number }>({});
  const [metadata, setMetadata] = useState<ScraperMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAndMapTarifs = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      console.log('[useScrapedTarifs] Chargement des tarifs scrapés...');
      
      const response = await scraperService.getTarifsLegalin(forceRefresh);

      if (!response.success) {
        throw new Error('Échec du chargement des tarifs');
      }

      const scraped = response.data;
      setScrapedData(scraped);
      setMetadata(response.metadata ?? null);

      console.log('[useScrapedTarifs] Mapping des données...');
      const mapped = TarifsMapperService.mapScrapedTarifs(scraped);
      setMappedTarifs(mapped);

      const prices = TarifsMapperService.generateBasePrices(mapped);
      setBasePrices(prices);

      console.log('[useScrapedTarifs] Données chargées et mappées:', {
        scrapedTypes: scraped.types_annonce.length,
        mappedTypes: Object.keys(prices).length,
        source: response.metadata?.source
      });

    } catch (err: any) {
      console.error('[useScrapedTarifs] Erreur:', err);
      setError(err.message || 'Erreur lors du chargement');

      // En cas d'erreur, utiliser les données de fallback
      console.warn('[useScrapedTarifs] Utilisation des tarifs de secours');
      const fallbackMapping = TarifsMapperService.mapScrapedTarifs(null);
      setMappedTarifs(fallbackMapping);
      setBasePrices(TarifsMapperService.generateBasePrices(fallbackMapping));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTarifs = useCallback(async () => {
    await loadAndMapTarifs(true);
  }, [loadAndMapTarifs]);

  // Chargement initial
  useEffect(() => {
    loadAndMapTarifs(false);
  }, [loadAndMapTarifs]);

  return {
    scrapedData,
    mappedTarifs,
    basePrices,
    metadata,
    loading,
    error,
    refreshTarifs
  };
};